// @ts-strict-ignore
import {Component, Injector} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {CameraResolution, CameraResolutionType, VideoDestinationFormat, WebCameraView} from "@models";
import {SimCameraService, SimEnumExtendedService, SimMaterialDialogService, NotificationService, SimTrainingRoomService} from "@services";

import Enumerable from "linq";
import {Translations} from "@translations";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {typeNodeToValueExpr} from "@angular/compiler-cli/src/ngtsc/reflection";
import {result, values} from "lodash-es";

export interface DialogData_CameraResolutionDialog extends MatDialogDataBaseInput {
	camera: WebCameraView;
}

export interface DialogData_CameraResolutionDialogOutput extends MatDialogDataBaseOutput {
	changedCamera?: WebCameraView;
}

interface CameraResolutionFormData {
	type: FormControl<CameraResolutionType>;
	outputFormat: FormControl<VideoDestinationFormat>;
	width: FormControl<number>;
	height: FormControl<number>;
	framerate: FormControl<number>;
	useCameraProxy: FormControl<boolean>;
	compression?: FormControl<number>;
	name: FormControl<string>;
}

@Component({
	selector: "sim-camera-resolution-dialog",
	templateUrl: "./camera-resolution-dialog.component.html",
	styleUrls: ["./camera-resolution-dialog.component.scss"],
	standalone: false,
})
export class CameraResolutionDialogComponent extends BasicMaterialDialog<
	CameraResolutionDialogComponent,
	DialogData_CameraResolutionDialog,
	DialogData_CameraResolutionDialogOutput
> {
	T = Translations;
	CameraResolutionType = CameraResolutionType;
	public cameraType: string = null;
	public resolutions: CameraResolution[] = null;
	public cameraDestinationFormats: {
		name: string;
		value: VideoDestinationFormat;
	}[] = null;
	public supportedResolutionsValidation: string = "";
	public supportedResolutionsText: string = "";
	private doReload: boolean = false;
	private supportedResolutions: any[] = [];
	columnsToDisplay: string[] = ["resolutionType", "outputFormat", "width", "height", "framerate", "compression", "useProxy"];
	form: FormGroup<{
		camerasArray: FormArray<FormGroup<CameraResolutionFormData>>;
	}>;

	constructor(
		private _simCameraService: SimCameraService,
		private _fb: FormBuilder,
		private _simEnumService: SimEnumExtendedService,
		private _simTrainingRoomService: SimTrainingRoomService,
		private _simNotificationService: NotificationService,
		private _simMatDialogService: SimMaterialDialogService,
		private _notificationService: NotificationService,
		_injector: Injector,
	) {
		super(_injector);

		this.resolutions = this.data.camera.configuration.resolutions;
		this.cameraType = this.data.camera.configuration.type;

		this.initializeCameraDestinationFormats();
		this.initializeCameraResolutions();
		this.createForm();
	}

	assertType(item: FormGroup<CameraResolutionFormData>) {
		return item;
	}

	public createForm(): void {
		let camerasArray = this._fb.array(
			Enumerable.from(this.data.camera.configuration.resolutions)
				.select((res) => this._fb.group(res))
				.toArray(),
		);
		this.form = this._fb.group({camerasArray});
	}

	public updateAllCamerasOfType() {
		this._simMatDialogService
			.openDeleteDialog({
				deleteTitle: "Change all Camera Resolutions",
				deleteText: `Do you really want to update all resolutions for all cameras of type "${this.cameraType}"?`,
				deleteButtonText: "Yes",
				cancelText: "Cancel",
			})
			.then((data) => {
				if (data.action === "ok") {
					this._simCameraService
						.updateAllCameraResolutionsOfType({
							resolutions: this.resolutions,
							type: this.cameraType,
						})
						.then(() => {
							this.doReload = true;
						});
				}
			});
	}

	updateAllCameras() {
		this._simMatDialogService
			.openDeleteDialog({
				deleteTitle: "Change all Camera Resolutions",
				deleteText:
					"Do you really want to update all resolutions for all cameras? Please keep in mind that some device types like Epiphans and Wifi-Cameras may not support certain resolutions.",
				deleteButtonText: "Yes",
				cancelText: "Cancel",
			})
			.then((data) => {
				if (data.action === "ok") {
					this._simCameraService
						.updateAllCameraResolutions({
							resolutions: this.data.camera.configuration.resolutions,
						})
						.then(
							() => {
								this._notificationService.notifySuccess();
							},
							() => {
								this._notificationService.notifyError("Error updating Camera");
							},
						)
						.finally(() => {
							this.doReload = true;
						});
				}
			});
	}

	ok() {
		this.close({action: "ok", changedCamera: this.data.camera});
	}

	private initializeCameraResolutions() {
		this._simCameraService.getCameraResolutions(this.data.camera.id).then((getCameraResolutionsResult) => {
			this.supportedResolutions = getCameraResolutionsResult;

			if (this.supportedResolutions && this.supportedResolutions.length > 0) {
				this.supportedResolutionsValidation =
					"|" +
					Enumerable.from(this.supportedResolutions)
						.select((supportedResolution) => {
							return supportedResolution.name;
						})
						.toArray()
						.join("|") +
					"|";
				this.supportedResolutionsText =
					"Supported resolutions: " +
					Enumerable.from(this.supportedResolutions)
						.select((supportedResolution) => {
							return supportedResolution.name;
						})
						.toArray()
						.join(", ");
			} else {
				this.supportedResolutionsValidation = "";
				this.supportedResolutionsText = "No supported resolutions.";
			}
		});
	}

	public initializeCameraDestinationFormats() {
		this._simEnumService.getEnumByName("VideoDestinationFormat").then((getEnumByNameResult) => {
			this.cameraDestinationFormats = [];
			getEnumByNameResult.forEach((v) => {
				this.cameraDestinationFormats.push({
					name: v.name,
					value: v.intValue,
				});
			});
		});
	}

	public updateAllValue(type: "OutputFormat" | "Framerate" | "Compression") {
		this._simMatDialogService
			.openUpdateDialog({
				typeOfSelector: type,
			})
			.then((result) => {
				if (result?.action === "ok") {
					switch (type) {
						case "OutputFormat":
							for (let i = 0; i <= this.resolutions.length; i++) {
								this.data.camera.configuration.resolutions[i].outputFormat = result.selectedValue;
							}
							break;
						case "Framerate":
							for (let i = 0; i <= this.resolutions.length; i++) {
								this.data.camera.configuration.resolutions[i].framerate = result.selectedValue;
							}
							break;
						case "Compression":
							for (let i = 0; i <= this.resolutions.length; i++) {
								this.data.camera.configuration.resolutions[i].compression = result.selectedValue;
							}
							break;
					}
				}
			});
	}
}
